<template>
  <div class="material">
    <div v-if="material">
      <hero-small v-if="productGroup" :background="handleHeroBackground()">
        <h1 class="text-white bold m-0 text-uppercase">
          {{material.acf.custom_banner_text ? material.acf.custom_banner_text : productGroup.name}}
        </h1>
      </hero-small>
      <div class="py-5">
        <div v-if="productCategory && productGroup && materialType" class="container">
          <div class="row">
            <div class="col-12">
              <div class="text-uppercase breadcrumbs">
                {{materialType.name}} > {{productCategory.acf.short_name}} <span v-html="productCategory.name"></span> >
                {{productGroup.name}} > <span class="bold" v-html="material.title.rendered"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container">
        <div class="row">
          <div class="col-12 col-lg-9">
            <div class="text-center py-5" v-if="materialLoading">
              <b-spinner type="grow" variant="secondary"></b-spinner>
            </div>
            <div v-else>
              <article id="material-wrapper">
                <div v-if="material.template === 'template-steps-with-numbers.php'">
                  <TemplateStepsWithNumbers :content="material" :documentGenerating="isDocumentGenerating"/>
                </div>
                <div v-if="material.template === 'template-steps-with-icons.php'">
                  <TemplateStepsWithIcons :content="material" :documentGenerating="isDocumentGenerating"/>
                </div>
                <div v-if="material.template === 'template-steps-with-icons-flexible.php'">
                  <TemplateStepsWithIconsFlexible :content="material" :documentGenerating="isDocumentGenerating"/>
                </div>
                <div v-if="material.template === 'template-flexible-steps.php'">
                  <TemplateFlexibleSteps :content="material" :documentGenerating="isDocumentGenerating"/>
                </div>
                <div v-if="material.template === 'template-video.php'">
                  <TemplateSingleVideo :content="material"/>
                </div>
                <div v-if="material.template === 'template-table.php'">
                  <TemplateTable :content="material"/>
                </div>
                <div class="export-loading-screen" v-if="showPrintLoadingScreen" :class="{'mobile': isMobile()}" id="print-loading">
                  <div class="text-center">
                    <p>Loading...</p>
                    <b-spinner type="grow" variant="secondary"></b-spinner>
                  </div>
                </div>
              </article>
              <div class="export-buttons mt-2 mb-5 d-flex justify-content-between flex-wrap flex-xl-nowrap">
                <div class="buttons-section d-flex">
                  <div v-if="material.template != 'template-video.php'">
                    <button @click="print">
                      <img src="@/assets/icons/printer.svg"/>
                      <span class="text-uppercase">{{$t('common.print')}}</span>
                    </button>
                  </div>
                  <div v-if="material.template != 'template-video.php'">
                    <button @click="generatePdfAsImage">
                      <img src="@/assets/icons/download.svg" />
                      <span class="text-uppercase">{{$t('common.download')}}</span>
                      </button>
                  </div>
                  <div class="position-relative">
                    <button @click="pageShareVisible = !pageShareVisible">
                      <img src="@/assets/icons/envelope.svg" />
                      <span class="text-uppercase">{{$t('common.sendToAFriend')}}</span>
                    </button>
                    <transition name="fade">
                      <div v-if="pageShareVisible" class="page-share">
                        <PageShareForm />
                      </div>
                    </transition>
                  </div>
                </div>
                <div class="material-stats text-secondary d-flex flex-column flex-md-row justify-content-start align-items-md-center">
                  <div class="mb-3 mb-md-0 mr-md-4">
                    <MaterialStarRating :rating="materialRating" :materialId="material.id" />
                  </div>
                  <div>
                    {{$t('materialStats.views')}} <span class="bold">{{pageVisits}}</span>
                  </div>
                </div>
              </div>
              <div v-if="material.comment_status === 'open'">
                <CommentsSection :materialId="material.id"
                                 :commentCompetitionEnabled="material.metadata.comment_competition_enabled"/>
              </div>
              <div class="text-center py-5" v-if="relatedMaterialsLoading">
                <b-spinner type="grow" variant="secondary"></b-spinner>
              </div>
              <div v-if="relatedMaterials" class="related-materials pt-4 pt-lg-5">
                <h3 class="mb-4 text-uppercase">{{$t('common.relatedMaterials')}}</h3>
                <div>
                  <div v-for="material in relatedMaterials" :key="material.id"
                      class="mb-4">
                    <SearchItem :material="material" />
                  </div>
                  <div id="related-materials-loading-more"
                       class="py-1"
                       v-view="handleLoadingMoreRelatedMaterials" >
                    <div v-if="relatedMaterialsLoadingMore">
                      <div class="mb-4">
                        <SearchItemSkeleton />
                      </div>
                      <div class="mb-4">
                        <SearchItemSkeleton />
                      </div>
                    </div>
                  </div>
                  <div v-if="!relatedMaterialsReachedTheEnd && !relatedMaterialsLoadingMore && isMobile()" class="text-center">
                    <button class="btn btn-secondary mb-5 text-uppercase"
                            @click="getRelatedMaterialsByTags(material.tags, true)">
                        {{$t('common.loadMore')}}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-3">
            <div class="sidebar-back-home mb-4">
              <div class="buttons">
                <div class="button mr-4" @click="goBack">
                  <img src="@/assets/icons/step_back.svg" />
                  <span v-html="$t('common.stepBack')"></span>
                </div>
                <div class="button" @click="reset">
                  <img src="@/assets/icons/new_search.svg"  class="mr-1"/>
                  <span v-html="$t('common.newSearch')"></span>
                </div>
              </div>
            </div>
            <SidebarCategories v-if="isCategoriesLoaded"
                               :materialType="materialType"
                               :productCategory="productCategory"
                               :productGroup="productGroup"
                               :currentMaterialId="material.id"/>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="materialNotFoundInSelectedLanguage">
      <b-modal id="modal-no-material"
                no-close-on-backdrop v-model="materialNotFoundInSelectedLanguage">
        <template #modal-header>
          <h5 class="text-md bold mb-0">{{ $t('common.materialNotFound') }}</h5>
        </template>
        <p>
          {{ $t('common.materialDoesNotExistInLang') }}<br>
          {{ $t('common.clickOkToChangeLang') }}
        </p>
        <template #modal-footer>
          <div class="d-flex">
            <button class="btn btn-light btn-rounded text-sm px-4 mr-3"
                    @click="goHome()">
              {{ $t('common.cancel') }} ({{ goHomeTimeCount }})
            </button>
            <button class="btn btn-secondary btn-rounded text-sm px-4"
                    @click="redirectToEngMaterial()">
              OK
            </button>
          </div>
        </template>
      </b-modal>
    </div>
    <div class="text-center py-5" v-else>
      <b-spinner type="grow" variant="secondary"></b-spinner>
    </div>
    <div class="export-loading-screen" v-if="showDownloadLoadingScreen" :class="{'mobile': isMobile()}" id="download-loading">
      <div class="text-center">
        <p>Loading...</p>
        <b-spinner type="grow" variant="secondary"></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import HeroSmall from '@/components/HeroSmall.vue';
import SearchItem from '@/components/SearchItem.vue';
import SearchItemSkeleton from '@/components/SearchItemSkeleton.vue';
import PageShareForm from '@/components/PageShareForm.vue';
import TemplateStepsWithNumbers from '@/templates/TemplateStepsWithNumbers.vue';
import TemplateStepsWithIcons from '@/templates/TemplateStepsWithIcons.vue';
import TemplateStepsWithIconsFlexible from '@/templates/TemplateStepsWithIconsFlexible.vue';
import TemplateFlexibleSteps from '@/templates/TemplateFlexibleSteps.vue';
import TemplateSingleVideo from '@/templates/TemplateSingleVideo.vue';
import TemplateTable from '@/templates/TemplateTable.vue';
import SidebarCategories from '@/components/SidebarCategories.vue';
import MaterialStarRating from '@/components/MaterialStarRating.vue';
import CommentsSection from '@/components/CommentsSection.vue';
import { jsPDF } from 'jspdf';

const htmlToImage = require('html-to-image');
const imagesLoaded = require('imagesloaded');

export default {
  components: {
    HeroSmall,
    SearchItem,
    SearchItemSkeleton,
    TemplateStepsWithNumbers,
    TemplateStepsWithIcons,
    TemplateStepsWithIconsFlexible,
    TemplateFlexibleSteps,
    TemplateSingleVideo,
    TemplateTable,
    SidebarCategories,
    PageShareForm,
    MaterialStarRating,
    CommentsSection,
  },
  data() {
    return {
      pageTitle: 'Nissens Support',
      material: null,
      materialType: null,
      productCategory: null,
      productGroup: null,
      materialLoading: false,
      isDocumentGenerating: false,
      showPrintLoadingScreen: false,
      showDownloadLoadingScreen: false,
      isCategoriesLoaded: false,
      relatedMaterials: null,
      relatedMaterialsLoading: false,
      relatedMaterialsLoadingMore: false,
      relatedMaterialsCurrentPage: 1,
      relatedMaterialsTotalPages: null,
      relatedMaterialsReachedTheEnd: false,
      scrolledToLoadMoreRelatedMaterials: false,
      pageVisits: null,
      materialRating: null,
      pageShareVisible: false,
      materialNotFoundInSelectedLanguage: false,
      materialInDefaultLanguageUrl: '',
      goHomeTimeCount: 10,
      goHomeTimeoutId: undefined,
    };
  },
  metaInfo() {
    return {
      title: this.pageTitle,
    };
  },
  methods: {
    async getMaterial() {
      if (this.$route.params.slug) {
        this.materialLoading = true;

        const material = await this.$http
          .get(`${process.env.VUE_APP_API_URL}/materials?lang=${this.$i18n.locale}&slug=${this.$route.params.slug}`);

        if (!material.data[0]) {
          // Check if post with given slug exists in any other language, then redirect if available
          try {
            const basedMaterial = await this.$http
              .get(`${process.env.VUE_APP_API_URL}/materials?slug=${this.$route.params.slug}`);

            if (basedMaterial) {
              const materialSlug = basedMaterial.data[0].translations[this.$i18n.locale];
              if (materialSlug) {
                this.$router.push(`/material/${materialSlug}`);
              } else if (basedMaterial.data[0].translations.en) {
                this.materialInDefaultLanguageUrl = basedMaterial.data[0].translations.en;
                this.materialNotFoundInSelectedLanguage = true;
                this.goHomeAfterTimeout();
              } else {
                throw new Error();
              }
            }
          } catch {
            this.$router.push('/');
          }
        } else {
          this.setupPage(material.data[0]);
        }
      }
    },
    async setupPage(data) {
      this.material = data;
      this.pageTitle = `${this.material.title.rendered} - Nissens Support`;
      if (
        this.$route.params.productCategory && this.$route.params.productGroup && this.$route.params.materialType
      ) {
        this.productCategory = this.$route.params.productCategory;
        this.productGroup = this.$route.params.productGroup;
        this.materialType = this.$route.params.materialType;
        // When this.material.categories is array type, it means backend returns more than 3 categories
      } else if (Array.isArray(this.material.categories)) {
        this.productGroup = {
          id: 0,
          name: this.$t('common.allNissensProducts'),
        };
      } else {
        const [materialType, productCategory, productGroup] = await Promise.all([
          this.$http.get(
            `${process.env.VUE_APP_API_URL}/material_categories/${this.material.categories.material_type.term_id}`,
          ),
          this.$http.get(
            `${process.env.VUE_APP_API_URL}/material_categories/${this.material.categories.product_category.term_id}`,
          ),
          this.$http.get(
            `${process.env.VUE_APP_API_URL}/material_categories/${this.material.categories.product_group.term_id}`,
          ),
        ]);
        this.materialType = materialType.data;
        this.productCategory = productCategory.data;
        this.productGroup = productGroup.data;
      }
      if (this.material.tags.length) {
        this.getRelatedMaterialsByTags(this.material.tags);
      }
      if (this.material.acf.stats) {
        this.pageVisits = Number(this.material.acf.stats.page_visits) + 1;
        this.updatePageVisits(this.pageVisits);
        this.materialRating = Number(this.material.acf.stats.average_rating);
      } else {
        this.pageVisits = 1;
        this.updatePageVisits(this.pageVisits);
        this.materialRating = 0;
      }
      this.materialLoading = false;
      this.isCategoriesLoaded = true;
    },
    handleLoadingMoreRelatedMaterials(e) {
      if (e.type === 'enter' && !this.relatedMaterialsReachedTheEnd && !this.isMobile()) {
        this.getRelatedMaterialsByTags(this.material.tags, true);
      }
    },
    async getRelatedMaterialsByTags(tags, loadingMore = false) {
      if (loadingMore) {
        this.relatedMaterialsCurrentPage += 1;
        this.relatedMaterialsLoadingMore = true;
        if (this.isMobile()) {
          this.$scrollTo('#related-materials-loading-more', 600, { offset: -50 });
        }
      } else {
        this.relatedMaterialsLoading = true;
      }
      const paginationParams = {
        page: this.relatedMaterialsCurrentPage,
        per_page: 5,
      };
      const params = {
        lang: this.$i18n.locale,
        tags: tags.join(),
        exclude: this.material.id,
        ...paginationParams,
      };
      const relatedMaterials = await this.$http
        .get(`${process.env.VUE_APP_API_URL}/materials`, { params });
      if (relatedMaterials.data.length) {
        if (loadingMore) {
          this.relatedMaterials = this.relatedMaterials.concat(relatedMaterials.data);
          this.relatedMaterialsLoadingMore = false;
        } else {
          this.relatedMaterials = relatedMaterials.data;
          this.relatedMaterialsLoading = false;
        }
        this.relatedMaterialsTotalPages = Number(relatedMaterials.headers['x-wp-totalpages']);
      } else {
        this.relatedMaterialsLoading = false;
      }
      this.relatedMaterialsReachedTheEnd = this.relatedMaterialsCurrentPage === this.relatedMaterialsTotalPages;
    },
    updatePageVisits(count) {
      this.$http.post(`${process.env.VUE_APP_API_SUPPORT_URL}/page_visit`, { value: count, material_id: this.material.id });
    },
    handleHeroBackground() {
      if (this.material.acf.header_image) {
        return this.material.acf.header_image;
      }
      if (this.productGroup.acf && this.productGroup.acf.highlight_image) {
        return this.productGroup.acf.highlight_image.sizes['2048x2048'];
      }
      if (Array.isArray(this.material.categories) && this.material.featured_image.large && !this.productGroup.id) {
        return this.material.featured_image.large;
      }
      return false;
    },
    prepareMaterialToExport() {
      this.isDocumentGenerating = true;

      // Disable lazy loading images and load them immediately
      const images = document.querySelectorAll('#material-wrapper img[loading=lazy]');
      if (images.length) {
        images.forEach((image) => {
          image.removeAttribute('loading');
          image.removeAttribute('srcset');
        });
      }

      const downloadsSection = document.querySelector('.downloads');
      if (downloadsSection) {
        downloadsSection.style.display = 'none';
      }

      const videos = document.querySelectorAll('.video-wrapper');
      if (videos.length) {
        videos.forEach((video) => {
          video.style.display = 'none';
        });
      }
    },
    generatePdfAsImage() {
      this.showDownloadLoadingScreen = true;
      this.prepareMaterialToExport();

      const body = document.querySelector('body');
      body.style.overflow = 'hidden';
      const viewPort = document.getElementById('viewport');
      viewPort.setAttribute('content', 'width=1920');

      const materialWrapper = document.getElementById('material-wrapper');
      setTimeout(() => {
        htmlToImage.toJpeg(materialWrapper, {
          quality: 1,
          backgroundColor: 'white',
          pixelRatio: 1,
        })
          .then((dataUrl) => {
            const i = new Image();
            i.src = dataUrl;
            i.onload = () => {
              /* eslint-disable */
              const doc = new jsPDF({
                unit: 'pt',
                format: [i.width * 0.75, i.height * 0.75],
                useCORS: false,
              });
              doc.addImage(dataUrl, 'JPEG', 0, 0);
              doc.save(`${this.material.title.rendered}.pdf`);
              this.isDocumentGenerating = false;
              setTimeout(() => {
                this.showDownloadLoadingScreen = false;
                if (videos.length) {
                  videos.forEach((video) => {
                    video.style.display = 'block';
                  });
                }
                body.style.overflow = 'unset';
                viewPort.setAttribute('content', 'width=device-width,initial-scale=1.0');
              }, 500);
            };
          });
      }, 500);
    },
    print() {
      this.showPrintLoadingScreen = true;
      this.prepareMaterialToExport();

      setTimeout(() => {
        const materialWrapper = document.querySelector('#material-wrapper').innerHTML;
        const appWrapper = document.querySelector('#app');
        appWrapper.innerHTML = '';
        appWrapper.innerHTML = '<div id="material-wrapper">'+materialWrapper+'</div>';
        imagesLoaded( '#material-wrapper', function() {
          document.getElementById('print-loading').remove();
          window.print();
        });
      }, 500);

      window.addEventListener('afterprint', () => {
        window.location.reload(true);
      });
    },
    redirectToEngMaterial() {
      this.$router.push(`/en/material/${this.materialInDefaultLanguageUrl}`);
      this.materialNotFoundInSelectedLanguage = false;
    },
    reset() {
      this.$store.dispatch('threeSteps/reset');
      this.$router.push({name: 'Home'});
    },
    goBack() {
      this.$router.go(-1);
    },
    goHome() {
      this.clearGoHomeTimeout();
      this.$router.push({name: 'Home'});
    },
    goHomeAfterTimeout() {
      this.goHomeTimeoutId = setTimeout(() => {
        this.goHome();
      }, this.goHomeTimeCount * 1000);
      setInterval(() => {
        if (this.goHomeTimeCount > 0) {
          this.goHomeTimeCount -= 1;
        }
      }, 1000);
    },
    clearGoHomeTimeout() {
      if (this.goHomeTimeoutId) {
        clearTimeout(this.goHomeTimeoutId);
      }
    }
  },
  created() {
    window.scroll(0, 0);
    this.getMaterial();
  },
  watch: {
    $route() {
      this.clearGoHomeTimeout();
      this.relatedMaterials = null;
      this.relatedMaterialsCurrentPage = 1;
      this.relatedMaterialsTotalPages = null;
      this.relatedMaterialsLoadingMore = false;
      this.relatedMaterialsReachedTheEnd = false;
      this.getMaterial();
      window.scroll(0, 0);
    },
  },
};
</script>
<style lang="scss">
@import '@/assets/scss/common.scss';
.breadcrumbs {
  font-size: 12px;
}
#material {
  background-color: white;
}
.export-loading-screen {
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
  p {
    font-size: 20px;
  }
  span.spinner-border {
    width: 35px;
    height: 35px;
  }
  &.mobile {
    p {
      font-size: 5vw;
    }
    span.spinner-border {
      width: 7vw;
      height: 7vw;
    }
  }
}
.export-buttons {
  border-bottom: 1px solid $secondary;
  padding: 2rem 0;
  .buttons-section {
    width: 51%;
    justify-content: space-between;
  }
  .page-share {
    position: absolute;
    width: 300px;
    left: 0;
    top: 46px;
    background: white;
  }
  @include media-breakpoint-down(lg) {
    .buttons-section {
      width: 100%;
      justify-content: flex-start;
      margin-bottom: 1.5rem;
    }
    .page-share {
      position: unset;
    }
  }
  @include media-breakpoint-down(sm) {
    .buttons-section {
      flex-direction: column;
    }
  }
  button {
    background-color: white;
    border: none;
    display: flex;
    align-items: center;
    padding: 5px 10px;
    color: $secondary;
    img {
      width: 30px;
      margin-right: 10px;
    }
  }
}
.material-stats {
  padding: 0 10px;
}
.sidebar-back-home {
  .buttons {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    .button {
      display: flex;
      align-items: center;
      cursor: pointer;
      justify-content: flex-start;
      width: 100%;
      img {
        max-width: 30px;
      }
      span {
        font-size: 12px;
        line-height: 1.2em;
        color: $secondary;
      }
    }
    @include media-breakpoint-down(lg) {
      margin-top: 20px;
      span {
        font-size: 10px!important;
      }
    }
  }
}
</style>
